// One event's Seat Statuses, for the admin's seat blocking page (IDV-705): the Sections grouped under their Sectors,
// each with its grid and its counts by status. Every GET of the statuses is loaded whole, and tells which seats
// changed since the last one.
import { computed, reactive, ref, type Ref } from 'vue'
import { SeatStatus, SeatStatusLabels } from '@/constants/SeatStatuses'
import type { EventSeat, EventSeatRow } from '@/types/EventSeats'
import { plural } from '@/helpers/seatEditor/editor'

export { plural }

export interface BoardRow {
  id: string
  symbol: string
  /** 1 is the Row closest to the stage */
  order: number
  seats: EventSeat[]
}

export interface BoardSection {
  id: string
  name: string
  sectorId: string
  rows: BoardRow[]
  /** Grid width: the highest Seat Position */
  columns: number
}

export interface BoardSector {
  id: string
  name: string
  order: number
  sections: BoardSection[]
}

export type StatusCounts = Record<SeatStatus, number>

/** A seat whose status changed outside this page between two loads */
export interface StatusChange {
  seat: EventSeat
  sectionId: string
  from: SeatStatus
  to: SeatStatus
}

export interface Notice {
  text: string
  kind: 'info' | 'warning' | 'error' | 'success'
}

/** "A01, A02, A03"; past `max` names, "A01, A02 e mais 5" */
export function joinNames(names: string[], max = 6): string {
  const more = names.length > max ? ` e mais ${names.length - max}` : ''
  return `${names.slice(0, max).join(', ')}${more}`
}

/**
 * What a reload changed, named for the admin: the seats of the open Section first ("ZC01 reservado"), the other
 * Sections only counted; plus the selected seats that had to leave the selection. Null when nothing changed.
 */
export function pollNotice(changes: StatusChange[], sectionId: string, dropped: string[]): Notice | null {
  if (!changes.length) return null

  const describe = (change: StatusChange): string => `${change.seat.full_label} ${SeatStatusLabels[change.to].toLowerCase()}`
  const here = changes.filter(change => change.sectionId === sectionId)
  const elsewhere = changes.length - here.length

  const verb = plural(changes.length, 'lugar mudou', 'lugares mudaram')
  const list = here.length
    ? `${joinNames(here.map(describe))}${elsewhere ? ` e mais ${elsewhere} em outras Seções` : ''}`
    : joinNames(changes.map(describe))
  const where = here.length ? '' : ' em outras Seções'
  const left = dropped.length ? ` ${joinNames(dropped)} ${dropped.length === 1 ? 'saiu' : 'saíram'} da seleção.` : ''

  return { text: `Atualizado: ${verb}${where} (${list}).${left}`, kind: dropped.length ? 'warning' : 'info' }
}

/** A block or unblock refused with 409: nothing was applied, and these seats weren't in the expected status */
export function conflictNotice(labels: string[]): Notice {
  const one = labels.length === 1
  const changed = one ? 'mudou' : 'mudaram'
  const left = one ? 'ele saiu' : 'eles saíram'

  return {
    text: `Nada mudou: ${joinNames(labels)} ${changed} de status nesse meio-tempo. A grade foi atualizada e ${left} da seleção; aplique de novo para os demais.`,
    kind: 'error',
  }
}

const byName = (a: { name: string }, b: { name: string }): number => a.name.localeCompare(b.name, 'pt-BR')

export function emptyCounts(): StatusCounts {
  return { [SeatStatus.AVAILABLE]: 0, [SeatStatus.BLOCKED]: 0, [SeatStatus.RESERVED]: 0, [SeatStatus.OCCUPIED]: 0 }
}

function countSeats(seats: EventSeat[]): StatusCounts {
  const counts = emptyCounts()
  seats.forEach((seat) => {
    counts[seat.status]++
  })
  return counts
}

/** Sectors in the Mapping's order, their Sections by name, each Section's Rows front to back */
function group(rows: EventSeatRow[]): BoardSector[] {
  const sectors = new Map<string, BoardSector>()
  const sections = new Map<string, BoardSection>()

  rows.forEach((row) => {
    let sector = sectors.get(row.sector_id)
    if (!sector) {
      sector = { id: row.sector_id, name: row.sector_name, order: row.sector_order, sections: [] }
      sectors.set(row.sector_id, sector)
    }

    let section = sections.get(row.section_id)
    if (!section) {
      section = { id: row.section_id, name: row.section_name, sectorId: row.sector_id, rows: [], columns: 0 }
      sections.set(row.section_id, section)
      sector.sections.push(section)
    }

    const seats = [ ...row.seats ].sort((a, b) => a.position - b.position)
    section.rows.push({ id: row.id, symbol: row.symbol, order: row.order, seats })
    section.columns = Math.max(section.columns, ...seats.map(seat => seat.position))
  })

  sections.forEach(section => section.rows.sort((a, b) => a.order - b.order))
  sectors.forEach(sector => sector.sections.sort(byName))

  return [ ...sectors.values() ].sort((a, b) => a.order - b.order || byName(a, b))
}

export function useSeatBoard() {
  const sectors: Ref<BoardSector[]> = ref([])

  const sections = computed<Map<string, BoardSection>>(() => new Map(
    sectors.value.flatMap(sector => sector.sections.map(section => [section.id, section] as const)),
  ))

  /** Every seat by id, with its Section's id */
  const seats = computed<Map<string, { seat: EventSeat, sectionId: string }>>(() => new Map(
    [ ...sections.value.values() ].flatMap(section => section.rows.flatMap(row => row.seats.map(
      seat => [seat.id, { seat, sectionId: section.id } ] as const,
    ))),
  ))

  const sectionCounts = computed<Map<string, StatusCounts>>(() => new Map(
    [ ...sections.value.values() ].map(section => [section.id, countSeats(section.rows.flatMap(row => row.seats))]),
  ))

  const totals = computed<StatusCounts>(() => {
    const counts = emptyCounts()
    sectionCounts.value.forEach((section) => {
      Object.values(SeatStatus).forEach((status) => {
        counts[status] += section[status]
      })
    })
    return counts
  })

  /** Replaces the statuses with a fresh GET's; returns the seats that changed since the last load */
  function load(rows: EventSeatRow[]): StatusChange[] {
    const before = new Map([ ...seats.value ].map(([id, { seat } ]) => [id, seat.status]))
    sectors.value = group(rows)

    return [ ...seats.value.values() ].flatMap(({ seat, sectionId }) => {
      const from = before.get(seat.id)
      return from && from !== seat.status ? [ { seat, sectionId, from, to: seat.status } ] : []
    })
  }

  /** Sets the statuses this page changed itself, so the next load doesn't report them as changed elsewhere */
  function setStatuses(seatIds: string[], status: SeatStatus) {
    seatIds.forEach((id) => {
      const found = seats.value.get(id)
      if (found) found.seat.status = status
    })
  }

  function seat(id: string): EventSeat | undefined {
    return seats.value.get(id)?.seat
  }

  /** The seat's Row symbol and label ("ZC08"), or "" for a seat the board doesn't have */
  function label(id: string): string {
    return seat(id)?.full_label ?? ''
  }

  function statusOf(id: string): SeatStatus | undefined {
    return seat(id)?.status
  }

  function section(id: string): BoardSection | undefined {
    return sections.value.get(id)
  }

  function counts(sectionId: string): StatusCounts {
    return sectionCounts.value.get(sectionId) ?? emptyCounts()
  }

  return reactive({
    sectors,
    totals,
    load,
    setStatuses,
    seat,
    label,
    statusOf,
    section,
    counts,
  })
}

export type SeatBoard = ReturnType<typeof useSeatBoard>
